import NiceModal, { useModal } from '@ebay/nice-modal-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useInboxMessages } from '../../hooks/queries/useInboxMessages'
import useDimensions from '../../hooks/useDimensions'
import type { MessageObject } from '../../types/inbox'
import classNames from '../../utils/classNames'
import closeModal from '../../utils/closeModal'
import Modal from '../common/modal'
import Icon from '../icon'
import Empty from './empty'
import LoadMore from './load-more'
import Message from './message'
import MessageDetail from './message-detail'
import InboxMessageListSkeleton from './message-skeleton'

// The inbox as an overlay rather than the /profile/inbox route: routing there
// remounts whatever is behind it (a running game is torn down, any other page
// reloads), while a modal leaves the page untouched — closing it puts the
// player back exactly where they were. Same list / read-a-message flow as the
// page, just layered on top of it.
const InboxModal = NiceModal.create(() => {
  const modal = useModal()
  const { isDesktop } = useDimensions()
  const { t } = useTranslation('inbox')
  const [selected, setSelected] = useState<MessageObject | null>(null)
  // Pause the list query while a message is open and refetch on the way back,
  // so the row picks up its new read state — same as the inbox page.
  const {
    messages,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInboxMessages({ enabled: !selected })

  const renderList = () => {
    if (isFetching && !messages.length) return <InboxMessageListSkeleton />
    if (!messages.length) return <Empty className="py-10" />
    return (
      <>
        {messages.map(item => (
          <Message key={item.id} {...item} onSelect={setSelected} />
        ))}
        {hasNextPage && (
          <LoadMore
            onClick={() => void fetchNextPage()}
            isLoading={isFetchingNextPage}
          />
        )}
      </>
    )
  }

  return (
    <Modal
      id="inbox-modal"
      // Full screen on mobile (a phone has no room for a boxed dialog and the
      // messages need the height); a roomy centred dialog on desktop.
      isFullScreen={!isDesktop}
      containerStyle={classNames(
        'flex flex-col overflow-hidden !pt-4 !pb-4 !px-4',
        isDesktop && '!w-[520px] !max-w-[520px] max-h-[80vh]',
      )}>
      {/* Header: back to the list from a message, close the modal entirely */}
      <div className="flx-btw-ctr shrink-0 gap-3 pb-3 border-b border-solid border-secondary-txt-20">
        <div className="flx-ctr gap-2 min-w-0">
          {selected && (
            <button
              type="button"
              className="flex p-1 -ml-1"
              aria-label="Back to messages"
              onClick={() => setSelected(null)}>
              <Icon
                id="chevron-left"
                width="16"
                height="16"
                className="fill-highlight-gold"
              />
            </button>
          )}
          <span className="text-base leading-5 text-highlight-gold font-bold font-[Poppins] uppercase truncate">
            {t('header')}
          </span>
        </div>
        <button
          type="button"
          className="flex p-1 -mr-1"
          aria-label="Close messages"
          onClick={() => closeModal(modal)}>
          <Icon
            id="close"
            width="15"
            height="15"
            strokeWidth={0.8}
            className="fill-highlight-gold stroke-highlight-gold"
          />
        </button>
      </div>
      {/* min-h-0 so this pane (not the modal) is what scrolls. The negative
          margin lets rows use the full width while the scrollbar stays clear
          of them. */}
      <div className="flex-1 min-h-0 mt-3 -mr-2 pr-2 overflow-y-auto">
        {selected ? <MessageDetail message={selected} /> : renderList()}
      </div>
    </Modal>
  )
})

export default InboxModal
